import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText, Eye, Send, Mail, Edit } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  draft:     "bg-slate-100 text-slate-600",
  sent:      "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-600",
};

const STATUS_LABEL = {
  draft:     "Draft",
  sent:      "Sent",
  cancelled: "Cancelled",
};

export default function ProvisionalItineraryList() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]  = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (search) params.search = search;
      const res = await axios.get(`${API}/provisional-itineraries`, {
        headers: getAuthHeaders(),
        params,
      });
      setItems(res.data || []);
    } catch {
      toast.error("Failed to load provisional itineraries");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Stats
  const total     = items.length;
  const sent      = items.filter(i => i.status === "sent").length;
  const draft     = items.filter(i => i.status === "draft").length;
  const paid      = items.filter(i => i.service_fee_type === "paid").length;

  // First travel date from segments
  const travelDate = (pi) => {
    const segs = pi.segments || [];
    if (!segs.length) return "—";
    const dates = segs.map(s => s.departure_date).filter(Boolean).sort();
    if (!dates.length) return "—";
    try {
      return new Date(dates[0]).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return dates[0]; }
  };

  // Route: first departure → last arrival
  const route = (pi) => {
    const segs = (pi.segments || []).filter(s => s.departure_date);
    if (!segs.length) return "—";
    const first = segs[0];
    const last  = segs[segs.length - 1];
    const dep = first.departure_airport_code || first.departure_city || "";
    const arr = last.arrival_airport_code  || last.arrival_city    || "";
    if (!dep && !arr) return "—";
    return `${dep} → ${arr}`;
  };

  const openPdf = async (pi) => {
    try {
      const res = await axios.post(`${API}/provisional-itineraries/${pi.id}/pdf`, {}, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("PDF generation failed");
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Provisional Itineraries</h1>
            <p className="text-sm text-slate-500 mt-1">Travel plans and visa-support documents</p>
          </div>
          <Button onClick={() => navigate("/provisional-itineraries/new")}
            className="bg-purple-600 hover:bg-purple-700 text-white gap-2">
            <Plus className="w-4 h-4" /> New Provisional Itinerary
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total",   value: total  },
            { label: "Draft",   value: draft  },
            { label: "Sent",    value: sent   },
            { label: "Paid Service", value: paid },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">{s.label}</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <Input
            placeholder="Search by PI number, customer…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-xs h-9 text-sm"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-9 text-sm"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["PI Ref", "Customer", "Travel Date", "Route", "Service Fee", "Invoice", "Status", "Sent", "Actions"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No provisional itineraries found.</td></tr>
              ) : items.map(pi => (
                <tr key={pi.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono font-semibold text-purple-700">{pi.pi_number}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{pi.customer_name}</td>
                  <td className="px-4 py-3 text-slate-600">{travelDate(pi)}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{route(pi)}</td>
                  <td className="px-4 py-3">
                    {pi.service_fee_type === "paid"
                      ? <span className="text-slate-800 font-mono text-xs">€{(pi.service_fee_amount || 0).toFixed(2)}</span>
                      : <span className="text-slate-400 text-xs">Free</span>}
                  </td>
                  <td className="px-4 py-3">
                    {pi.linked_invoice_number
                      ? <span
                          className="text-blue-600 hover:underline cursor-pointer font-mono text-xs"
                          onClick={() => navigate(`/invoices/${pi.linked_invoice_id}`)}
                        >{pi.linked_invoice_number}</span>
                      : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[pi.status] || "bg-slate-100 text-slate-500"}`}>
                      {STATUS_LABEL[pi.status] || pi.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {pi.email_sent_at
                      ? new Date(pi.email_sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button title="Edit" onClick={() => navigate(`/provisional-itineraries/${pi.id}`)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-purple-700">
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button title="View PDF" onClick={() => openPdf(pi)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-purple-700">
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                      <button title="View Detail" onClick={() => navigate(`/provisional-itineraries/${pi.id}`)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-purple-700">
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
